import { Arrays } from "./arrays";
import { Utils } from "./utils";

export class StringsInternal {

    byteToHex(byte: number): string {
        return ('0' + byte.toString(16)).slice(-2);
    }

    bytesToBase64(buffer: ArrayBuffer): string {
        let binary = '';
        let bytes = new Uint8Array(buffer);
        let len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCodePoint(bytes[i]);
        }
        return window.btoa(binary);
    }

    bytesToUtf8String(buffer: ArrayBuffer): string {
        let result = "";
        let i = 0;
        let c: number;
        let c2: number;
        let c3: number;

        let data = new Uint8Array(buffer);

        // If we have a BOM skip it
        if (data.length >= 3 && data[0] === 0xef && data[1] === 0xbb && data[2] === 0xbf) {
            i = 3;
        }

        while (i < data.length) {
            c = data[i];

            if (c < 128) {
                result += String.fromCodePoint(c);
                i++;
            } else if (c > 191 && c < 224) {
                if (i + 1 >= data.length) {
                    throw new Error("UTF-8 Decode failed. Two byte character was truncated.");
                }
                c2 = data[i + 1];
                result += String.fromCodePoint((c & 31) << 6 | c2 & 63);
                i += 2;
            } else {
                if (i + 2 >= data.length) {
                    throw new Error("UTF-8 Decode failed. Multi byte character was truncated.");
                }
                c2 = data[i + 1];
                c3 = data[i + 2];
                result += String.fromCodePoint((c & 15) << 12 | (c2 & 63) << 6 | c3 & 63);
                i += 3;
            }
        }
        return result;
    }

    base64ToArrayBuffer(base64: string): ArrayBuffer {
        let binary_string = window.atob(base64);
        let len = binary_string.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.codePointAt(i);
        }
        return bytes.buffer;
    }

    combine(s1: string, s2: string, sep: string): string {
        return !Utils.isNoe(s1) && !Utils.isNoe(s2) ? s1 + sep + s2 : this.def(s1) + this.def(s2);
    }

    crop(s: string, length: number, ellipses = true): string {
        let e = ellipses ? " ..." : "";
        return s.length > length ? s.slice(0, length - e.length) + e : s;
    }

    def(s: string, def = ""): string {
        return !Utils.isNoe(s) ? s : def;
    }

    endsWithCi(s1: string, s2: string): boolean {
        return s1.toUpperCase().endsWith(s2.toUpperCase());
    }

    equalsCi(s1: string, s2: string): boolean {
        return s1.localeCompare(s2, "de", { sensitivity: "base" }) === 0;
    }

    ensureSuffix(s: string, suffix: string): string {
        return s.endsWith(suffix) ? s : s + suffix;
    }

    escapeHtml(unsafe: string): string {
        return Strings.replaceAll(Strings.replaceAll(Strings.replaceAll(Strings.replaceAll(Strings.replaceAll(unsafe, "&", "&amp;"), "<", "&lt;"), ">", "&gt;"), "\"", "&quot;"), "'", "&#039;");
    }

    escapeHttpQueryParam(value: string): string {
        return encodeURIComponent(value);
    }

    getRowAndColFromPos(s: string, pos: number): [number, number] {
        let sub = s.slice(0, pos);
        let lines = sub.split("\n");
        return [lines.length, sub.length - sub.lastIndexOf("\n")];
    }

    getSubstring(s: string, from: number, to: number): string {
        return s.slice(from >= 0 ? from : 0, to <= s.length ? to : s.length);
    }

    getSubstringBetween(s: string, from: string, to: string): string {
        return this.getSubstringTo(this.getSubstringFrom(s, from), to);
    }

    getSubstringCount(s: string, sub: string): number {
        if (!sub) {
            return 0;
        }

        let count = 0;
        let pos = 0;

        while (true) {
            pos = s.indexOf(sub, pos);
            if (pos === -1) {
                break;
            }
            count++;
            pos += sub.length;
        }

        return count;
    }

    getSubstringFrom(s: string, from: string, allIfNotFound = true): string {
        let index = s.indexOf(from);
        return index === -1 ? allIfNotFound ? s : "" : s.slice(index + from.length);
    }

    getSubstringFromLast(s: string, from: string, allIfNotFound = true): string {
        let index = s.lastIndexOf(from);
        return index === -1 ? allIfNotFound ? s : "" : s.slice(index + from.length);
    }

    getSubstringTo(s: string, to: string, allIfNotFound = true): string {
        let index = s.indexOf(to);
        return index === -1 ? allIfNotFound ? s : "" : s.slice(0, index);
    }

    getSubstringToLast(s: string, to: string, allIfNotFound = true): string {
        let index = s.lastIndexOf(to);
        return index === -1 ? allIfNotFound ? s : "" : s.slice(0, index);
    }

    hash(s: string): number {
        let a = 0;
        for (const ch of s) {
            const cp = ch.codePointAt(0) ?? 0;
            a = (a << 5) - a + cp;
            a |= 0; 
        }
        return a;
    }

    includesCi(s1: string, s2: string): boolean {
        return s1.toLowerCase().includes(s2.toLowerCase());
    }

    isInt(s: string): boolean {
        return Number.isInteger(Number(s));
    }

    isRegexValid(regex: string): boolean {
        try {
            // eslint-disable-next-line no-new
            new RegExp(regex);
            return true;
        }
        catch {
            return false;
        }
    }

    is(object: any): boolean {
        return typeof object === "string";
    }

    lowerFirst(s: string): string {
        return s.length == 0 ? s : s.charAt(0).toLowerCase() + s.slice(1);
    }

    matchesFullTextQuery(value: string, query: string): boolean {
        query = query.toUpperCase();
        return Utils.isNoe(query) || value.toUpperCase().includes(query);
    }

    nullIfEmpty(s: string): string {
        return Utils.isNoe(s) ? null : s;
    }

    removePrefix(s: string, prefix: string, recursive = false): string {
        if (Utils.isNoe(prefix)) {
            return s;
        }
        while (s.startsWith(prefix)) {
            s = s.slice(prefix.length);
            if (!recursive) {
                break;
            }
        }
        return s;
    }

    regexGetGroup(on: string, regex: RegExp, groupIndex = 0): string {
        try {
            const matches = on.matchAll(regex);

            // eslint-disable-next-line no-unreachable-loop
            for (let match of matches) {
                return Arrays.getSafe(match, groupIndex);
            }
            return null;
        }
        catch (error) {
            throw error instanceof Error && error.message?.includes("String.prototype.matchAll called with a non-global RegExp argument") ? new Error("Der reguläre Ausdruck " + regex + " muss global sein. Meldung: " + error.message) : error;
        }
    }

    regexGetGroups(on: string, regex: RegExp, groupIndex = 0): string[] {
        try {
            const matches = on.matchAll(regex);

            let result: string[] = [];
            for (let match of matches) {
                let g = Arrays.getSafe(match, groupIndex);
                if (g != null) {
                    result.push(g);
                }
            }
            return result;
        }
        catch (error) {
            throw error instanceof Error && error.message?.includes("String.prototype.matchAll called with a non-global RegExp argument") ? new Error("Der reguläre Ausdruck " + regex + " muss global sein. Meldung: " + error.message) : error;
        }
    }

    regexMatches(s: string, regex: string, caseInsensitive = true): boolean {
        return new RegExp("^" + regex + "$", caseInsensitive ? "i" : "").test(s);
    }

    removeSuffix(s: string, suffix: string, recursive = false): string {
        if (Utils.isNoe(suffix)) {
            return s;
        }
        while (s.endsWith(suffix)) {
            s = s.slice(0, s.length - suffix.length);
            if (!recursive) {
                break;
            }
        }
        return s;
    }

    replaceAll(s: string, pattern: string, replace: string): string {
        return s == null ? null : s.replace(new RegExp(pattern, 'g'), replace);
    }

    sep2(item1: string, item2: string, sep: string): string {
        return !Utils.isNoe(item1) && !Utils.isNoe(item2) ? item1 + sep + item2 : item1 + item2;
    }

    split(s: string, delim: string): string[] {
        return Utils.isNoe(s) ? [] : s.split(delim);
    }

    splitLines(s: string): string[] {
        return Utils.isNoe(s) ? [] : s.split(/\r?\n/);
    }

    splitOnce(s: string, delim: string): string[] {
        return s == null ? [] : s.includes(delim) ? [this.getSubstringTo(s, delim), this.getSubstringFrom(s, delim)] : [s];
    }

    splitRegex(s: string, regex: string): string[] {
        return Utils.isNoe(s) ? [] : s.split(new RegExp(regex));
    }

    startsWithCi(s1: string, s2: string): boolean {
        return s1.toUpperCase().startsWith(s2.toUpperCase());
    }

    stripBom(s: string): string {
        return s.codePointAt(0) === 0xFEFF ? s.slice(1) : s;
    }

    toCharArray(s: string): string[] {
        let result = [];
        for (const element of s) {
            result.push(element);
        }
        return result;
    }

    toColoredHtmlString(s: string, color: number): string {
        let col = Utils.toColorHexString(color);
        return "<span style=\"background-color: " + col + "; color: " + Utils.getTextColor(col) + "\">" + s + "</span>";
    }

    upperFirst(s: string): string {
        return s.length == 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
    }
}

export let Strings = new StringsInternal();